import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Landing.css';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import '../components/Loader.css';
import { PUBLIC, GOLD_LT, CREAM, INK } from '../theme';
import CollectionsCarousel from './CollectionsCarousel';
import useCollections from '../data/useCollections';
import { imageUrl } from '../data/imageUrl';
import useCategories from '../data/useCategories';
import useReducedMotion from '../components/useReducedMotion';
import FeaturedPieces from './FeaturedPieces';
import AtelierPromise from './AtelierPromise';
import AppointmentBand from './AppointmentBand';
import { categoryImageUrl } from '../data/categoryImageUrl';
import { responsiveImage, COVER_WIDTHS } from '../data/responsiveImage';

export default function Landing() {
  const location = useLocation();
  const { collections, loading: collectionsLoading, error: collectionsError } = useCollections();
  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories();
  const reducedMotion = useReducedMotion();

  const heroWrap = useRef(null);
  const heroMedia = useRef(null);
  const videoRef = useRef(null);
  const h1Ref = useRef(null);
  const h2Ref = useRef(null);
  const progRef = useRef(null);
  const carA = useRef(null);
  const carAReset = useRef(null);

  // ---- Hero scroll-scrub + crossfade ----
  useEffect(() => {
    // The scrub is the single largest source of motion on the site. When the
    // visitor has asked the OS to reduce motion, don't just skip the
    // animation — skip fetching and decoding the clip at all, and show the
    // second headline outright so they still get the full message.
    if (reducedMotion) {
      // The two headlines occupy the same frame and only ever coexist
      // because the scrub cross-fades between them. With no scrub, show the
      // opening one and leave the reveal hidden rather than stacking both.
      if (h1Ref.current) { h1Ref.current.style.opacity = '1'; h1Ref.current.style.transform = 'none'; }
      if (h2Ref.current) h2Ref.current.style.opacity = '0';
      if (progRef.current) progRef.current.style.transform = 'scaleX(1)';
      return undefined;
    }
    const wrap = heroWrap.current;
    const vid = videoRef.current;
    let raf = null;
    let bound = false;
    let pendingTime = null;
    let lastSeekAt = 0;
    const MIN_SEEK_INTERVAL = 33; // cap seek rate ~30/s — more than that just burns decoder budget with no visible benefit

    // On lower-throughput mobile decoders (seen on Android Chrome, esp. on
    // high-refresh-rate displays firing more scroll ticks/sec), issuing a
    // new seek before the last one finishes builds a backlog the video
    // never catches up on — it visibly lags behind and stutters forward.
    // Only ever have one seek in flight; once it resolves, jump straight to
    // the latest scroll position rather than working through stale ones.
    const onSeeked = () => {
      if (pendingTime === null || vid.seeking) return;
      const t = pendingTime;
      pendingTime = null;
      vid.currentTime = t;
    };
    if (vid) vid.addEventListener('seeked', onSeeked);

    // Load the clip as a blob so seeking (scroll-scrub) is reliable everywhere.
    let objectUrl = null;
    fetch(PUBLIC + '/assets/twirl.mp4')
      .then((r) => r.blob())
      .then((b) => {
        objectUrl = URL.createObjectURL(b);
        if (vid) {
          vid.src = objectUrl;
          vid.load();
          // iOS Safari won't render a seeked frame on a <video> that has
          // never played — it needs one real play() to spin up the decoder
          // before currentTime scrubbing has any visible effect.
          const prime = () => {
            vid.play().then(() => vid.pause()).catch(() => {});
            vid.removeEventListener('loadedmetadata', prime);
          };
          vid.addEventListener('loadedmetadata', prime);
        }
        update();
      })
      .catch(() => {});

    function update() {
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const total = wrap.offsetHeight - window.innerHeight;
      let p = total > 0 ? -rect.top / total : 0;
      p = Math.max(0, Math.min(1, p));

      if (vid && (vid.currentSrc || vid.src)) {
        const fade = Math.max(0, Math.min(1, (p - 0.16) / 0.22));
        vid.style.opacity = fade.toFixed(3);
        const vp = Math.max(0, Math.min(1, (p - 0.16) / 0.84));
        if (!isNaN(vid.duration) && vid.duration > 0) {
          const targetTime = vp * vid.duration;
          const now = performance.now();
          if (vid.seeking) {
            pendingTime = targetTime;
          } else if (Math.abs(vid.currentTime - targetTime) > 0.008) {
            if (now - lastSeekAt >= MIN_SEEK_INTERVAL) {
              lastSeekAt = now;
              vid.currentTime = targetTime;
            } else {
              pendingTime = targetTime;
            }
          }
        } else if (!bound) {
          bound = true;
          vid.addEventListener('loadedmetadata', update);
        }
      }
      if (h1Ref.current) {
        h1Ref.current.style.opacity = String(Math.max(0, 1 - p * 3.4));
        h1Ref.current.style.transform = `translateY(${(-p * 60).toFixed(1)}px)`;
      }
      if (h2Ref.current) {
        const q = Math.max(0, Math.min(1, (p - 0.62) / 0.32));
        h2Ref.current.style.opacity = q.toFixed(3);
        h2Ref.current.style.transform = `translate(-50%,-50%) scale(${(0.94 + q * 0.06).toFixed(3)})`;
      }
      if (progRef.current) progRef.current.style.transform = `scaleX(${p.toFixed(4)})`;
    }

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; update(); });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (vid) vid.removeEventListener('seeked', onSeeked);
      if (raf) cancelAnimationFrame(raf);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reducedMotion]);

  // ---- Auto-scrolling carousels (pause on hover / touch) ----
  useEffect(() => {
    const pairs = [[carA.current, carAReset.current]].filter(([el]) => el);
    const hover = new WeakSet();
    const listeners = [];
    // Safari quantizes scrollLeft to whole pixels on read, which silently
    // eats a sub-pixel `+=` increment every frame — the carousel just never
    // visibly moves there, even though the same code advances fine on
    // Chrome. Track the precise position ourselves instead of round-
    // tripping it through the DOM, and only ever write the accumulated
    // value (never read it back to accumulate further).
    const accum = new Map(pairs.map(([el]) => [el, el.scrollLeft]));

    // offsetLeft is a layout-dependent read. Reading it inside the 'scroll'
    // handler — which fires on every single auto-advance write, up to
    // ~60-120 times/sec — forces the browser to synchronously recompute
    // layout each time instead of batching it, competing with the page's
    // own scroll compositing and showing up as jank exactly while scrolling
    // past these sections. The loop width doesn't change frame to frame, so
    // measure it once (and on resize) instead of on every scroll tick.
    const loopWidths = new Map();
    const loopStates = new Map();
    const measureLoopWidths = () => {
      pairs.forEach(([el, resetEl]) => {
        if (!resetEl) return;
        // offsetLeft is relative to the nearest *positioned* ancestor, and
        // nothing between these cards and <body> sets `position`, so it
        // was actually measuring distance from the page edge, not from
        // this container — off by however far the carousel sits from the
        // left edge. getBoundingClientRect gives the true one-copy width.
        const loopWidth = resetEl.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
        loopWidths.set(el, loopWidth);
        // The list is rendered twice so the auto-scroll can wrap seamlessly,
        // but that only reads as a loop once the row actually overflows and
        // needs to scroll. With too few items a single copy already fits
        // the container, so the second copy just sits there fully visible —
        // every card doubled, not scrolling. Hide it in that case.
        const needsLoop = loopWidth > el.clientWidth;
        // Mobile browsers fire `resize` repeatedly while the address bar
        // shows/hides during scroll. Only touch the DOM when the
        // loop-needed state actually flips, so those resize events don't
        // force a reflow on every tick and stall an in-progress swipe.
        if (loopStates.get(el) === needsLoop) return;
        loopStates.set(el, needsLoop);
        for (let node = resetEl; node; node = node.nextElementSibling) {
          node.style.display = needsLoop ? '' : 'none';
        }
      });
    };
    measureLoopWidths();
    window.addEventListener('resize', measureLoopWidths);

    pairs.forEach(([el]) => {
      const enter = () => hover.add(el);
      const leave = () => hover.delete(el);
      el.addEventListener('mouseenter', enter);
      el.addEventListener('mouseleave', leave);
      el.addEventListener('touchstart', enter, { passive: true });
      el.addEventListener('touchend', () => setTimeout(leave, 2500), { passive: true });

      // The item list is rendered twice back-to-back; once scrollLeft passes
      // one copy's width, silently rewind by that width. The duplicate is
      // pixel-identical to the original, so the wrap is invisible. This
      // runs on every scroll event — not just the auto-advance tick — so a
      // manual drag/wheel past the seam wraps immediately too, instead of
      // briefly revealing the duplicate half and snapping back once the
      // tick resumes.
      const onScroll = () => {
        const loopWidth = loopWidths.get(el);
        if (loopWidth && loopWidth > 1) {
          if (el.scrollLeft >= loopWidth) el.scrollLeft -= loopWidth;
          else if (el.scrollLeft < 0) el.scrollLeft += loopWidth;
        }
        // Re-sync our accumulator to the DOM's actual value after every
        // scroll (ours or a manual drag) so the two never drift apart.
        accum.set(el, el.scrollLeft);
      };
      el.addEventListener('scroll', onScroll, { passive: true });

      listeners.push([el, enter, leave, onScroll]);
    });
    // Auto-advance is motion the visitor did not initiate, so it stops
    // outright under reduce. The rows stay scrollable by drag, wheel and
    // keyboard — the content is still fully reachable, it just holds still.
    const SPEED = 0.55;
    let raf;
    const tick = () => {
      pairs.forEach(([el]) => {
        if (hover.has(el)) return;
        const next = accum.get(el) + SPEED;
        accum.set(el, next);
        el.scrollLeft = next;
      });
      raf = requestAnimationFrame(tick);
    };
    if (!reducedMotion) raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', measureLoopWidths);
      listeners.forEach(([el, enter, leave, onScroll]) => {
        el.removeEventListener('mouseenter', enter);
        el.removeEventListener('mouseleave', leave);
        el.removeEventListener('scroll', onScroll);
      });
    };
    // Re-runs once collections finish loading so carAReset (only rendered
    // once real items replace the loading placeholders) is measured — see
    // CollectionsCarousel.
  }, [collections.length, reducedMotion]);

  // ---- Scroll to in-page section when arriving via a cross-page hash link ----
  useEffect(() => {
    if (!location.hash) return;
    const el = document.querySelector(location.hash);
    if (el) el.scrollIntoView();
  }, [location]);

  const categoriesShowPlaceholders = categoriesLoading && categories.length === 0;

  return (
    <div style={{ fontFamily: "'Cormorant Garamond', serif", background: INK, color: '#efe4d2' }}>

      <Nav />

      <main id="ij-main">

      {/* HERO */}
      <header ref={heroWrap} style={{ position: 'relative', height: reducedMotion ? 'calc(var(--vh, 1vh) * 100)' : 'calc(var(--vh, 1vh) * 360)', background: INK }}>
        <div style={{ position: 'sticky', top: 0, height: 'calc(var(--vh, 1vh) * 100)', overflow: 'hidden' }}>
          <div ref={heroMedia} style={{ position: 'absolute', inset: 0, transformOrigin: '52% 46%' }}>
            {/* The poster frame is painted immediately and is not lazy, so it
                sets how fast the page appears on a phone. As a PNG this
                photograph was 2.3MB; the same crop as WebP is 186KB. The PNG
                stays as the fallback source. */}
            <picture>
              <source srcSet={PUBLIC + '/assets/hero-palace.webp'} type="image/webp" />
              <img src={PUBLIC + '/assets/hero-palace.png'} alt="Bride twirling in a candlelit Mughal palace" fetchpriority="high" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: '52% 45%' }} />
            </picture>
            <video ref={videoRef} muted playsInline preload="auto" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', opacity: 0 }} />
          </div>

          {/* colour grade — the bottom stop is heavier than it looks it needs
              to be because on a phone the headline and standfirst both land on
              the embroidered skirt, which is the busiest, highest-contrast
              region of the frame. */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(28,14,6,.28) 0%,transparent 30%,transparent 46%,rgba(12,7,4,.55) 76%,rgba(10,6,3,.9) 100%)', pointerEvents: 'none' }} />
          {/* A second wash from the left holds the opening headline, which is
              set against the palace arches rather than empty ground. */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,rgba(10,6,3,.78) 0%,rgba(10,6,3,.45) 32%,transparent 62%)', pointerEvents: 'none' }} />

          {/* candle glows */}
          {[
            { top: '6%', left: '4%', s: 180, o: .5, dur: 3.1, delay: 0 },
            { top: '14%', left: '31%', s: 150, o: .42, dur: 2.4, delay: .6 },
            { top: '44%', left: '15%', s: 90, o: .4, dur: 2.8, delay: .3 },
            { top: '24%', right: '26%', s: 80, o: .35, dur: 3.4, delay: .9 },
            { top: '44%', right: '6%', s: 70, o: .32, dur: 2.6, delay: 1.2 },
          ].map((g, i) => (
            <div key={i} style={{ position: 'absolute', top: g.top, left: g.left, right: g.right, width: g.s, height: g.s, borderRadius: '50%', background: `radial-gradient(circle,rgba(226,190,118,${g.o}),transparent 70%)`, filter: 'blur(6px)', animation: `ijflicker ${g.dur}s ease-in-out ${g.delay}s infinite`, pointerEvents: 'none' }} />
          ))}

          {/* vignette + gold frame */}
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(125% 105% at 52% 42%,transparent 32%,rgba(9,6,3,.72) 100%)', pointerEvents: 'none' }} />
          {/* Inset is responsive: a fixed 20/26px double rule eats 52px of a
              320px screen, which is what pushed the headline through it. */}
          <div className="ij-hero-frame" />
          <div className="ij-hero-frame ij-hero-frame-inner" />

          {/* initial headline */}
          <div ref={h1Ref} style={{ position: 'absolute', left: 'clamp(40px,6vw,90px)', right: 'clamp(40px,6vw,90px)', bottom: 'clamp(64px,13vh,150px)', maxWidth: 560, zIndex: 5 }}>
            <h1 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontSize: 'clamp(44px,6.6vw,96px)', lineHeight: .94, margin: 0, color: CREAM, textShadow: '0 2px 30px rgba(0,0,0,.6)' }}>Woven in the{' '}<br /><em style={{ fontWeight: 500, color: GOLD_LT }}>light of empires.</em></h1>
            <p style={{ fontSize: 'clamp(18px,2vw,24px)', fontStyle: 'italic', color: 'rgba(239,228,210,.82)', margin: '22px 0 0', maxWidth: 440 }}>Bridal couture for the modern maharani — heirloom craft, cut for today.</p>
          </div>

          {/* reveal headline */}
          {/* This line lands dead centre, straight over the model and the
              gold-on-red embroidery — the one place in the frame with no
              tonal contrast to spare. The drama stays; a soft radial scrim
              rides with the text so the words survive whichever frame of the
              scrub is underneath them. */}
          <div ref={h2Ref} style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 'min(calc(100vw - 96px),760px)', textAlign: 'center', zIndex: 5, opacity: 0, pointerEvents: 'none' }}>
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: '150%', height: '260%', background: 'radial-gradient(closest-side ellipse at center, rgba(9,6,3,.82) 0%, rgba(9,6,3,.66) 42%, rgba(9,6,3,.28) 72%, transparent 100%)', pointerEvents: 'none' }}
            />
            <h2 style={{ position: 'relative', fontFamily: "'Bodoni Moda', serif", fontWeight: 700, fontSize: 'clamp(40px,7vw,110px)', lineHeight: .9, margin: 0, color: CREAM, textShadow: '0 4px 40px rgba(0,0,0,.85), 0 1px 3px rgba(0,0,0,.7)' }}>Every twirl,{' '}<br /><em style={{ fontWeight: 500, color: GOLD_LT }}>a dynasty.</em></h2>
          </div>

          {/* progress */}
          <div style={{ position: 'absolute', left: 0, bottom: 0, width: '100%', height: 3, background: 'rgba(201,162,75,.18)', zIndex: 6 }}>
            <div ref={progRef} style={{ height: '100%', width: '100%', background: 'linear-gradient(90deg,#c9a24b,#e2be76)', transform: 'scaleX(0)', transformOrigin: 'left' }} />
          </div>
        </div>
      </header>

      {/* COLLECTIONS */}
      <CollectionsCarousel
        collections={collections}
        imageUrl={imageUrl}
        carouselRef={carA}
        resetRef={carAReset}
        loading={collectionsLoading}
        error={collectionsError}
      />

      {/* FEATURED PIECES — actual garments, which the homepage never showed */}
      <FeaturedPieces categories={categories} loading={categoriesLoading} />

      {/* CATEGORIES */}
      <section id="categories" style={{ padding: 'clamp(64px,10vh,130px) clamp(20px,4vw,60px)', background: 'linear-gradient(180deg,#26100f 0%,#180b0a 100%)', color: CREAM, borderTop: '1px solid rgba(201,162,75,.2)' }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '.36em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 14 }}>III — Shop by Category</div>
          <h2 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontStyle: 'italic', fontSize: 'clamp(38px,5.4vw,74px)', lineHeight: .98, margin: 0, color: CREAM }}>Explore by occasion</h2>
        </div>
        {/* A continuously drifting marquee. The previous scrollLeft-based
            version measured whether one copy of the list overflowed its
            container and hid the duplicate set when it didn't — with four
            categories one copy is ~1280px against ~1440px of container, so it
            always took that branch and there was nothing left to scroll. This
            drives the loop off a transform instead, so it works at any item
            count: the list is rendered twice and the track slides exactly one
            copy's width before repeating, which reads as seamless. */}
        <div className="ij-cat-row">
        <div className="ij-cat-track">
          {categoriesShowPlaceholders && Array.from({ length: 4 }).map((_, i) => (
            <div key={`ph-${i}`}>
              <div style={{ position: 'relative', width: '100%', aspectRatio: '4 / 5', background: 'repeating-linear-gradient(135deg,#3a2418,#3a2418 14px,#301c12 14px,#301c12 28px)', border: '1px solid rgba(226,190,118,.35)' }}>
                <div className="ij-loader-wrap"><span className="ij-loader" /></div>
              </div>
            </div>
          ))}

          {!categoriesShowPlaceholders && categoriesError && categories.length === 0 && (
            <p style={{ color: 'rgba(243,234,214,.6)', fontSize: 15 }}>Categories are temporarily unavailable.</p>
          )}
          {!categoriesShowPlaceholders && !categoriesError && categories.length === 0 && (
            <p style={{ color: 'rgba(243,234,214,.6)', fontSize: 15 }}>No categories published yet.</p>
          )}

          {!categoriesShowPlaceholders && categories.length > 0 && [...categories, ...categories].map((cat, i) => {
            const cover = cat.coverImage;
            const isClone = i >= categories.length;
            return (
              <Link
                key={`${cat.slug}-${i}`}
                to={`/categories/${cat.slug}`}
                className="ij-cat-card"
                // Second copy exists only so the loop has no visible seam.
                aria-hidden={isClone ? 'true' : undefined}
                tabIndex={isClone ? -1 : undefined}
                style={{ textDecoration: 'none', display: 'block', color: 'inherit' }}
              >
                <div style={{ position: 'relative', width: '100%', aspectRatio: '4 / 5', background: 'repeating-linear-gradient(135deg,#3a2418,#3a2418 14px,#301c12 14px,#301c12 28px)', border: '1px solid rgba(226,190,118,.35)', overflow: 'hidden' }}>
                  {cover && (() => {
                    const img = responsiveImage(
                      categoryImageUrl(cover),
                      COVER_WIDTHS,
                      '(max-width: 640px) 92vw, (max-width: 1100px) 46vw, 22vw'
                    );
                    return (
                      <img
                        src={img.src}
                        srcSet={img.srcSet}
                        sizes={img.sizes}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                      />
                    );
                  })()}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(24,11,10,.32) 0%,transparent 42%)' }} />
                </div>
                {/* Set below the image rather than over it. The covers run from
                    a near-black bridal green to a pale sage pret, so no single
                    overlay treatment held the label legibly across all four —
                    and it is the pattern the rest of the category
                    grids already use. */}
                <div style={{ marginTop: 16 }}>
                  <h3 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontSize: 'clamp(26px,2.8vw,34px)', lineHeight: 1, margin: '0 0 6px', color: CREAM }}>{cat.name}</h3>
                  <span style={{ fontSize: 14, letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(243,234,214,.62)' }}>{cat.dresses.length} piece{cat.dresses.length === 1 ? '' : 's'}</span>
                </div>
              </Link>
            );
          })}
        </div>
        </div>
        <div style={{ marginTop: 36 }}>
          <Link to="/categories" style={{ display: 'inline-block', fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase', color: GOLD_LT, textDecoration: 'none', borderBottom: '1px solid rgba(226,190,118,.4)', padding: '4px 0 8px' }}>View all categories →</Link>
        </div>
      </section>

      <AtelierPromise />

      <AppointmentBand />

      </main>

      <Footer />

    </div>
  );
}
